import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { navConfig } from '../../config/pages';
import './Navigation.css';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const getScrollTop = () =>
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const handleScroll = () => {
      const nextScrolled = getScrollTop() > 0;
      setIsScrolled((prev) => (prev !== nextScrolled ? nextScrolled : prev));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const currentTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    setIsScrolled(currentTop > 0);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [menuOpen]);

  const scrollToHash = (hash) => {
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClick = (e, href) => {
    setMenuOpen(false);

    if (!href.includes('#')) {
      // Misma página: scrollear arriba manualmente (pathname no cambia, ScrollToTop no dispara)
      if (location.pathname === href) {
        e.preventDefault();
        document.getElementById('top')?.scrollIntoView({ behavior: 'smooth' });
      }
      // Cross-page: ScrollToTop se encarga vía useLayoutEffect
      return;
    }

    e.preventDefault();
    const [path, hash] = href.split('#');
    const targetPath = path || '/';

    if (location.pathname === targetPath) {
      scrollToHash(hash);
    } else {
      navigate(targetPath, { state: { scrollTo: hash } });
    }
  };

  const isActive = (href) => {
    if (href.includes('#')) return false;
    return location.pathname === href;
  };

  const renderLink = (item, className = 'navLink') => {
    const activeClass = isActive(item.href) ? 'active' : '';
    return (
      <Link
        to={item.href}
        className={`${className} ${activeClass}`}
        onClick={(e) => handleClick(e, item.href)}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <nav ref={navRef} className={`mainNav ${isScrolled ? 'is-scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="navContainer">
        <Link to="/" className="navLogoLink" onClick={(e) => handleClick(e, '/')}>
          <img
            src="/Images/logo-foreground-wide.png"
            alt="Estudio 811"
            className="navLogoImage"
          />
        </Link>

        <button
          className="navHamburger"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Abrir menú"
          aria-expanded={menuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <ul className="navMenu">
          {navConfig.links.map((item) => (
            <li key={item.href}>
              {renderLink(item)}
            </li>
          ))}
          {navConfig.cta && (
            <li>
              {renderLink(navConfig.cta, 'navLink navCta pillButton pillButton--on-dark')}
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
